import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Web3Provider } from '../providers/Web3Provider';
import { useAuthStore } from '../stores/auth.store';
import { useSubscriptionStore } from '../stores/subscription.store';
import { installTimezoneSync } from '../services/timezone-sync.service';
import {
  applyAnalyticsIdentity,
  setAnalyticsUserProperty,
  syncNativeAnalyticsConsent,
  trackGaPageView,
} from '../utils/ga';
import { configureConsent, trackUserEvent } from '../utils/reddit-pixel';
import AppToastContainer from './AppToastContainer';
import { ScrollToTopOnPush } from './ScrollToTopOnPush';

const queryClient = new QueryClient();

// Wire the Reddit Pixel consent checker to the auth store. Done at module
// scope so it runs once on first import — before any tracking call can fire.
// Lives here (rather than in reddit-pixel.ts itself) to avoid a circular
// import: auth.store imports from reddit-pixel.
//
// Policy: allow tracking for anonymous visitors (no logged-in user), and
// respect data_collection_consent for identified users. An opted-out
// logged-in user gets NO tracking, even for "anonymous" events like article
// views — because the Reddit cookie persists across auth state and would
// correlate the event back to their identity regardless of payload.
configureConsent(() => {
  const user = useAuthStore.getState().user;
  return !user || user.data_collection_consent === true;
});

// The device's time zone goes to the server for every signed-in user, on
// every platform: the server labels the times it prints in emails and pushes
// from it, and has nothing to go on for a web user otherwise (users.timezone
// is NULL = shown in UTC). Module scope, like the wiring below: once per app.
installTimezoneSync();

// GA phase 2: identity + native-consent live off the same auth subscription
// the consent checker reads. applyAnalyticsIdentity (utils/ga.ts, litmus-
// covered there) clears user_id/plan_tier for opted-out users — Codex P1
// on #816.
useAuthStore.subscribe((state, prev) => {
  if (state.user !== prev.user) {
    applyAnalyticsIdentity(state.user, useSubscriptionStore.getState().planId);
  }
  // Auth settled with no user (Codex rounds 4+5 on #816). Two cases, split
  // by TOKEN presence at settle time:
  //  - no token: genuinely anonymous — collection ON and the swallowed
  //    route replays, per the anonymous-visitor policy.
  //  - token still present: /me failed (offline, 5xx — initAuth's catch
  //    leaves the token in place so the next start retries it), so
  //    consent is STILL unknown. Stay dark — FAIL CLOSED. A returning
  //    opted-out user on a flaky network must not get collection enabled
  //    by a timeout; the cost is a rare fully-untracked session, which
  //    privacy wins. A later successful /me resolves via the user branch.
  if (prev.isLoading && !state.isLoading && !state.user && !state.token) {
    applyAnalyticsIdentity(null);
  }
});
// Boot state (Codex P1 on #816, round 2): auth persists only {token,
// isGuest} — a returning user boots with a token and NO user object until
// /me resolves, and treating that window as "anonymous" switched native
// collection ON for a possibly opted-out user. Token-without-user means
// consent UNKNOWN: native collection defaults OFF and the subscription
// corrects it the moment the user object lands.
//
// That OFF window swallows the initial native route RouteTracker fires
// (Codex P2, round 3) — so it is flagged here and REPLAYED by the
// subscription below once a CONSENTED user resolves. An opted-out user
// resolves the flag without a replay: nothing should be recorded for them.
{
  const boot = useAuthStore.getState();
  if (boot.token && !boot.user) {
    syncNativeAnalyticsConsent(false);
  } else {
    applyAnalyticsIdentity(boot.user, useSubscriptionStore.getState().planId);
  }
}

// plan_tier follows the AUTHORITATIVE subscription store (Codex P2 on
// #816): purchases update useSubscriptionStore.planId without touching
// auth.user, so auth-only wiring froze the tier at its login value through
// the exact conversion window that matters. Same consent gate.
useSubscriptionStore.subscribe((state, prev) => {
  if (state.planId === prev.planId) return;
  const auth = useAuthStore.getState();
  // isAuthenticated too (self-review on #816): a sign-out resets the
  // subscription store, and that reset must not re-write plan_tier onto the
  // just-anonymized client.
  if (auth.isAuthenticated && auth.user && auth.user.data_collection_consent === true) {
    setAnalyticsUserProperty('plan_tier', state.planId ?? null);
  }
});

/** Fires Reddit PageVisit on SPA route changes (initial load is handled by the base pixel in index.html). */
function RouteTracker() {
  const { pathname } = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    // Skip the first render — base pixel already fired PageVisit on load
    if (isFirst.current) {
      isFirst.current = false;
      // WEB: the base snippet records the initial view. NATIVE: the
      // consent-transition recorder in utils/ga.ts owns the initial screen
      // (it must chain after setEnabled anyway) — firing here too would
      // double count (Codex round 12 consolidation).
      return;
    }
    trackUserEvent('PageVisit');

    // Per-route title for the /app/* dashboard routes, which (unlike the public
    // pages) set no Helmet <title> — so without this they all report the static
    // index.html homepage title, collapsing ~99% of GA views into one row.
    // Public pages manage their own title via react-helmet-async; leave those.
    const appTitle = resolveAppTitle(pathname);
    if (appTitle) document.title = `${appTitle} - EmpathEase`;

    // Explicit GA4 page_view AFTER the title settles, so GA records the correct
    // per-route title — not the stale one GA4 Enhanced Measurement would capture
    // at history-change time. setTimeout(0) (a macrotask) runs AFTER React's
    // commit + Helmet's post-paint useEffect, unlike requestAnimationFrame
    // (which fires before paint, so document.title could still be stale) — and
    // unlike rAF, setTimeout is not paused in background tabs (middle-click "open
    // in new tab" still records the view).
    // NOTE: to avoid double-counting, turn OFF Enhanced Measurement's "page
    // changes based on browser history events" in the GA4 admin (this manual
    // page_view replaces it). See docs/plans/GA-INSTRUMENTATION.md.
    const timerId = window.setTimeout(() => {
      trackGaPageView(pathname, document.title);
    }, 0);
    return () => window.clearTimeout(timerId);
  }, [pathname]);
  return null;
}

// Readable titles for the authenticated /app/* routes (no Helmet of their own).
const APP_ROUTE_TITLES: Record<string, string> = {
  '/app': 'Course',
  '/app/progress': 'Progress',
  '/app/empathy': 'Empathy Queue',
  '/app/wallet': 'Wallet',
  '/app/tools': 'Tools',
  // Tool sub-routes: without these the segment fallback (index-2 = 'tools')
  // collapses them all to 'Tools'.
  '/app/tools/giraffe-translator': 'Giraffe Translator',
  '/app/tools/needs-guesser': 'Needs Guesser',
  '/app/profile': 'Profile',
};

/** Resolve a readable title for /app/* routes; null for public (Helmet) pages. */
function resolveAppTitle(pathname: string): string | null {
  // Exactly /app or a /app/ subpath — NOT public routes that merely start with
  // "app" (e.g. /approve, /applications).
  if (pathname !== '/app' && !pathname.startsWith('/app/')) return null;
  if (APP_ROUTE_TITLES[pathname]) return APP_ROUTE_TITLES[pathname];
  // Dynamic routes like /app/lesson/:id, /app/quiz/:id, /app/empy/:id → segment.
  const seg = pathname.split('/')[2];
  if (!seg) return 'App';
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

interface ProvidersProps {
  children: React.ReactNode;
}

// Providers are used to wrap the app in the necessary providers
function Providers({ children }: ProvidersProps) {
  // Note: Subscription initialization happens in auth.store.js after login
  // via fetchSubscription() called from initAuth()

  return (
    <>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Web3Provider>
            <BrowserRouter>
              <RouteTracker />
              <ScrollToTopOnPush />
              {children}
            </BrowserRouter>
          </Web3Provider>
        </QueryClientProvider>
      </HelmetProvider>
      <AppToastContainer />
    </>
  );
}

export default Providers;
